import {
  processForms, processRules, processSectionAndInputFields, processTabs,
} from '@app/utils/processForms/dataProcessing';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { FORM_FIELD_SUPPORTED_ENTITIES } from '@repo/crm-atoms';
import { type DataTypeConfigType } from '@repo/utils';

interface RuleValue {
  value: string[];
}

export type AssociateDataType = 'default' | 'dropdown';

export type DropdownComparatorsType = 'is' | 'is_defined' | 'contains_data';

export type FormComparatorsType = 'on_load';

export type ComparatorsType = DropdownComparatorsType | FormComparatorsType;

export enum RuleEntityTypes {
  INPUT_FIELD = 'Form::InputField',
  SECTION = 'Form::Section',
}

export interface Rule {
  id: string;
  sequenceNumber: number;
  comparator: ComparatorsType; // e.g., "is"
  value: RuleValue;
  entity: {
    id: string;
    type: RuleEntityTypes,
    inputField: {
      id: string;
      associate: {
        dataType: AssociateDataType;
      };
    } | null;
  }
}

export type ActionType = 'show_field' | 'make_mandatory';

export interface RuleActionOn {
  id: string;
  entity: {
    id: string;
    type: RuleEntityTypes;
  }
}

// RuleAction Structure
export interface RuleAction {
  id: string;
  sequenceNumber: number;
  actionType: ActionType; // e.g., "show_field", "make_mandatory"
  ruleActionsOn: RuleActionOn[];
  value: null | Record<'value', any>;
}

export enum AssociateTypes {
  LEAD_ATTRIBUTE = 'LeadAttribute',
  ACTIVITY_ATTRIBUTE = 'ActivityAttribute',
  TASK_ATTRIBUTE = 'Task::Attribute',
  INPUT_FIELD_LABEL_ATTRIBUTE = 'Form::InputFieldLabel',
}

export const ASSOCIATE_TYPE_FORM_ITEM_MAPPING = {
  [AssociateTypes.LEAD_ATTRIBUTE]: FORM_FIELD_SUPPORTED_ENTITIES.leadAttribute,
  [AssociateTypes.ACTIVITY_ATTRIBUTE]: FORM_FIELD_SUPPORTED_ENTITIES.activityAttribute,
  [AssociateTypes.TASK_ATTRIBUTE]: FORM_FIELD_SUPPORTED_ENTITIES.taskAttribute,
};

export interface RuleGroup {
  id: string;
  sequenceNumber: number;
  rules: Rule[];
  ruleActions: RuleAction[];
}

export enum AssociateTypeName {
  LEAD_ATTRIBUTE = 'LeadAttribute',
  ACTIVITY_ATTRIBUTE = 'ActivityAttribute',
  TASK_ATTRIBUTE = 'TaskAttribute',
  INPUT_FIELD_LABEL = 'InputFieldLabel',
}

export const ASSOCIATE_TYPE_NAME_FORM_ITEM_MAPPING = {
  [AssociateTypeName.LEAD_ATTRIBUTE]: FORM_FIELD_SUPPORTED_ENTITIES.leadAttribute,
  [AssociateTypeName.ACTIVITY_ATTRIBUTE]: FORM_FIELD_SUPPORTED_ENTITIES.activityAttribute,
  [AssociateTypeName.TASK_ATTRIBUTE]: FORM_FIELD_SUPPORTED_ENTITIES.taskAttribute,
};

export interface LeadAttribute {
  typeName: AssociateTypeName.LEAD_ATTRIBUTE;
  id: string;
  schemaName: string;
  dataType: string;
  isMandatory: boolean;
  dataTypeConfig?: DataTypeConfigType;
}

export interface ActivityAttribute {
  typeName: AssociateTypeName.ACTIVITY_ATTRIBUTE;
  id: string;
  schemaName: string;
  dataType: string;
  isMandatory: boolean;
  dataTypeConfig?: DataTypeConfigType;
}

export interface TaskAttribute {
  typeName: AssociateTypeName.TASK_ATTRIBUTE;
  id: string;
  schemaName: string;
  dataType: string;
  isMandatory: boolean;
  dataTypeConfig?: DataTypeConfigType;
}

export interface InputFieldLabel {
  typeName: AssociateTypeName.INPUT_FIELD_LABEL;
  id: string;
  label: string;
  value: string;
}

type AssociateType = LeadAttribute | ActivityAttribute | TaskAttribute | InputFieldLabel;

export interface InputField {
  id: string;
  displayName: string;
  placeholder: string;
  requiredField: boolean;
  sequenceNumber: number;
  associateType: `${AssociateTypes}`;
  associate: AssociateType;
  isHidden: boolean;
}

export interface Section {
  id: string;
  name: string;
  sequenceNumber: number;
  inputFields: InputField[];
}

export interface Tab {
  id: string;
  isHidden: boolean;
  createdAt: string; // ISO 8601 string for date
  sequenceNumber: number;
  sections: Section[];
}

export interface Form {
  id: string;
  name: string;
  leadFieldEnabled: boolean;
  visibility: string;
  tabs: Tab[];
  ruleGroups: RuleGroup[];
}

interface SetFormDataPayload {
  forms: Form[];
}

const initialState: {
  forms: Record<string, {
    form: {
      id: string;
      tabs: Array<string>;
    };
    currentTab: number;
    totalTabs: number;
    completed: boolean;
  } | undefined>;
  tabs: Record<string, {
    id: string;
    sections: Array<string>;
  }>;
  sections: Record<string, {
    id: string;
    name: string;
    inputFields: Array<string>;
  }>;
  inputFields: Record<string, InputField>;
  rules: Record<string, object>;
  leadUuid: string;
} = {
  forms: {},
  tabs: {},
  sections: {},
  inputFields: {},
  rules: {},
  leadUuid: '',
};

export const formDetailsSlice = createSlice({
  name: 'formDetails',
  initialState,
  reducers: {
    setFormData: (state, action: PayloadAction<{
      data: SetFormDataPayload;
      formName: string;
    }>) => {
      const {
        data,
        formName,
      } = action.payload;
      const forms = data.forms[0];
      if (forms) {
        const form = processForms(forms);
        const tabs = processTabs(forms);
        const {
          sections,
          inputFields,
        }: any = processSectionAndInputFields(forms);
        const rules = processRules(forms);

        state.forms[formName] = {
          form,
          currentTab: 0,
          totalTabs: form.tabs?.length || 0,
          completed: false,
        };
        state.tabs = {
          ...state.tabs,
          ...tabs,
        };
        state.sections = {
          ...state.sections,
          ...sections,
        };
        state.inputFields = {
          ...state.inputFields,
          ...inputFields,
        };
        state.rules = {
          ...state.rules,
          ...rules,
        };
      }
    },
    incrementCurrentTab: (state, action: PayloadAction<{
      formName: string;
    }>) => {
      const { formName } = action.payload;
      if (state.forms[formName]) {
        const {
          currentTab,
          totalTabs,
        } = state.forms[formName];

        state.forms[formName] = {
          ...state.forms[formName],
          currentTab: (currentTab + 1),
          completed: ((currentTab + 1) >= totalTabs),
        };
      }
    },
    resetCurrentTab: (state, action: PayloadAction<{
      formName: string;
    }>) => {
      const { formName } = action.payload;
      if (state.forms[formName]) {
        state.forms[formName] = {
          ...state.forms[formName],
          currentTab: 0,
          completed: false,
        };
      }
    },
    updateLeadUuid: (state, action: PayloadAction<string>) => {
      state.leadUuid = action.payload;
    },
  },
});

export const {
  setFormData,
  incrementCurrentTab,
  resetCurrentTab,
  updateLeadUuid,
} = formDetailsSlice.actions;

export default formDetailsSlice.reducer;
